/** Save of the tour draft (plan §3 `useTourApply`, D3): the Ledger's changeset compiled
 *  into the existing PATCH.
 *
 *  - **409 `tour_modified`**: refetch the detail (`staleTime: 0` — the new baselines
 *    reach the DraftProvider, whose `tourFreshValues` re-checks every entry), then
 *    compare this save's changes with it (`rebaseChanges`). Nothing drifted — a
 *    background `updated_ts` bump, an edit of other fields — → recompile on the fresh
 *    detail and resend ONCE with its `updated_ts`. Otherwise reject with `{drifted}`:
 *    the Ledger rebases those entries and shows its banner; Keep mine / Take theirs per
 *    field. A second 409 refetches and reports the same way.
 *  - **409 `easy_go_live_required`**: the tour's tenant is easy and its automatic guest
 *    messages are still blocked, so its launch policy changes only with Go live in its easy
 *    setup. Not a drift: no refetch, no resend — a `refusalFailure` with the words and a link
 *    per tenant; the draft stays as it was.
 *  - **400**: the nested errors flattened onto change keys (`tourValidationFailure`,
 *    positional collection errors through the compile `index`).
 *  - **Success**: `onSaved` first (the Ledger resets the draft before the response
 *    re-seeds the baselines), then the detail goes into the query cache and the staged
 *    files are dropped. The receipt is synthetic — the PATCH has no audit row to link
 *    (until B7) — and carries the pins the server could not apply as warnings. It and
 *    the draft's SAVED step cover only the changes the PATCH carried (`included`,
 *    `applied`): a change to a row the server no longer has is never claimed. */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MutableRefObject } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as toursApi from '@/api/tours';
import type { TourDetail, TourPatchResult } from '@/api/tours';
import type { DraftWireChange } from '@/features/records/draft/changeSet';
import { refusalFailure } from '@/features/records/editor/errors';
import type {
  RecordApplyMutation,
  RecordApplyResult,
  RecordChangeset,
} from '@/features/records/editor/types';
import type { StagedFiles } from '@/features/records/fields/widgets/stagedFiles';
import {
  easyGoLiveLinks,
  easyGoLiveRequired,
  easyGoLiveRequiredWords,
} from '@/features/tour-operators/onboard/easyGoLiveRequired';

import { projectTourBaseline, type TourBaseline } from './baselines';
import { compileTourPatch } from './compile';
import { rebaseChanges } from './drift';
import { tourValidationFailure } from './errors';

export const tourDetailKey = (id: number) => ['tours', 'detail', id] as const;

export function fetchTourDetail(id: number): Promise<TourDetail> {
  return toursApi.retrieve(id, { deleted: 'include' });
}

/** The optimistic-concurrency 409 (`tour_modified`) — not the PATCH's other 409,
 *  `easy_go_live_required`, which no refetch or resend can get past. */
export function isTourModified(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 409 && !easyGoLiveRequired(e);
}

/** What one PATCH sent, for the receipt and the 400 mapping. */
interface Sent {
  changes: readonly DraftWireChange[];
  result: TourPatchResult;
}

export function tourReceipt(sent: Sent): RecordApplyResult {
  const { result, changes } = sent;
  return {
    // Only what the PATCH carried leaves the draft (a change to a row the server no
    // longer has was not sent).
    applied: changes.map((c) => ({ rec: `${c.entity}:${c.id}`, path: c.field ?? c.action ?? '', to: c.to })),
    audit_id: null,
    audit_uuid: null,
    event_tag: null,
    saved_ts: result.updated_ts ?? new Date().toISOString(),
    changes: changes.map((c) => ({
      key: `${c.entity}:${c.id}:${c.field ?? c.action ?? ''}`,
      entity: c.entity,
      id: c.id,
      field: c.field ?? null,
      action: c.action ?? null,
      outcome: 'change',
      from: c.from,
      to: c.to,
    })),
    events: [],
    follow_ups: [],
    warnings: (result.pin_errors ?? []).map((p) => p.message),
  };
}

export interface TourApplyInput {
  tourId: number;
  /** The baseline of the latest loaded detail (a ref: read at save time). */
  baselineRef: MutableRefObject<TourBaseline>;
  files: StagedFiles;
  onSaved: (result: RecordApplyResult) => void;
}

export function useTourApply({ tourId, baselineRef, files, onSaved }: TourApplyInput): RecordApplyMutation {
  const qc = useQueryClient();

  const send = async (changes: readonly DraftWireChange[], base: TourBaseline): Promise<Sent> => {
    const compiled = compileTourPatch(changes, base, files);
    try {
      const result = toursApi.hasUploads(compiled.files)
        ? await toursApi.updateMultipart(tourId, compiled.body, compiled.files)
        : await toursApi.update(tourId, compiled.body);
      return { changes: compiled.included, result };
    } catch (e) {
      if (e instanceof ConsoleApiError && e.status === 400) {
        throw tourValidationFailure(e.body, { tourId, index: compiled.index, sent: changes });
      }
      const goLive = easyGoLiveRequired(e);
      if (goLive) {
        const ids = goLive.tourOperatorIds;
        throw refusalFailure(easyGoLiveRequiredWords(ids), easyGoLiveLinks(ids));
      }
      throw e;
    }
  };

  const refetch = async (): Promise<TourBaseline> => {
    const detail = await qc.fetchQuery({ queryKey: tourDetailKey(tourId), queryFn: () => fetchTourDetail(tourId), staleTime: 0 });
    return projectTourBaseline(detail);
  };

  const mutation = useMutation({
    mutationFn: async (body: RecordChangeset): Promise<Sent> => {
      const changes = body.changes as DraftWireChange[];
      try {
        return await send(changes, baselineRef.current);
      } catch (e) {
        if (!isTourModified(e)) throw e;
      }
      // 409: what moved under this save?
      const fresh = await refetch();
      const rebased = rebaseChanges(changes, fresh.paths);
      if (rebased.drifted.length > 0) throw { drifted: rebased.drifted };
      try {
        return await send(rebased.changes, fresh);
      } catch (e) {
        if (!isTourModified(e)) throw e;
      }
      // It moved again while resending: report drift if there is some, else give up.
      const again = rebaseChanges(changes, (await refetch()).paths);
      if (again.drifted.length > 0) throw { drifted: again.drifted };
      throw new Error('The tour changed again while saving — nothing was saved. Save again.');
    },
    onSuccess: (sent) => {
      onSaved(tourReceipt(sent));
      files.clear();
      const detail: TourDetail & { pin_errors?: unknown } = { ...sent.result };
      delete detail.pin_errors;
      qc.setQueryData(tourDetailKey(tourId), detail as TourDetail);
      void qc.invalidateQueries({ queryKey: ['tours'], predicate: (q) => q.queryKey[1] !== 'detail' });
    },
  });

  return { mutate: mutation.mutate, isPending: mutation.isPending };
}
